import { SessionAuth, getUserId } from 'supertokens-auth-react/recipe/session';
import { isApiError, patchFormData } from '@/services/httpClient';
import { retryAsync } from '@/utils/retryAsync';
import {
  TextInput,
  Group,
  Box,
  ColorPicker,
  Center,
  Modal,
  Paper,
  Avatar,
  Title,
  Text,
  Divider,
  ActionIcon,
  Tooltip,
  Card,
  FileButton,
  SegmentedControl,
  Switch,
  SimpleGrid,
  useMantineColorScheme,
  Notification,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useEffect, useRef } from 'react';
import { useStore, UserProfile } from '../store/Store';
import { useLanguageStore } from '../store/Store';
import { userPath, cdnCompanyLogos, serverPath, clientRoutes } from '../ApiPaths';
import { ColorSchemeToggle } from '../components/ColorSchemeToggle/ColorSchemeToggle';
import { PageHeader } from '@/components/PageHeader';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { NewsletterSubscription } from '../components/NewsletterSubscription';
import { pageWrapper } from '@/Styles';
import { validateHexColor, validateString } from '../services/Validators';
import ReactCrop, { Crop, centerCrop, makeAspectCrop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  IconUpload,
  IconUserCircle,
  IconBuildingSkyscraper,
  IconPalette,
  IconDeviceFloppy,
  IconUser,
  IconBuilding,
  IconSun,
  IconMoon,
  IconCheck,
  IconChevronDown,
  IconKey,
  IconRefresh,
} from '@tabler/icons-react';
import { EditorChipButton } from '@/components/EditorChipButton';
import accountClasses from './Account.module.css';
import { prominent } from 'color.js';
import { getAppSubscriptionInfosByUser } from '../Api';
import { IAppSubscriptionPublicInfo } from '../types/App.model';
import { useTranslation } from 'react-i18next';
import { validateFileSize } from '@/services/Exporters';
import { useNavigate } from 'react-router-dom';

// User license types interface
interface UserLicense {
  id: string;
  name: string;
  type: string;
  expiryDate: string;
  isActive: boolean;
}

// rectangle image crop https://github.com/sekoyo/react-image-crop https://codesandbox.io/p/sandbox/react-image-crop-demo-with-react-hooks-y831o?file=%2Fsrc%2FApp.tsx
export function Account() {
  const userProfile = useStore((state) => state.userProfile);
  const setUserProfile = useStore((state) => state.setUserProfile);
  const { language, setLanguage } = useLanguageStore();
  const [errorMessage, setErrorMessage] = useState('');
  const [openCropModal, setOpenCropModal] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const imgRef = useRef<HTMLImageElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [croppedImageFile, setCroppedImageFile] = useState<File | null>(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // State for color suggestions extracted from logo
  const [colorSuggestions, setColorSuggestions] = useState<string[]>([
    DEFAULT_QR_CODE_COLOR,
    '#008080',
    '#1c7ed6',
    '#E03131',
    '#7048e8',
    '#f59f00',
    '#228be6',
  ]);

  // New state for popup animation
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState(-100);
  const [popupOpacity, setPopupOpacity] = useState(0);

  // State for expandable color input section
  const [colorInputExpanded, setColorInputExpanded] = useState(false);

  // User licenses state
  const [userLicenses, setUserLicenses] = useState<IAppSubscriptionPublicInfo[]>([]);
  const [isLoadingLicenses, setIsLoadingLicenses] = useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  // Function to fetch licenses
  const fetchUserLicenses = async () => {
    if (!userProfile || !userProfile._id) return;

    setIsLoadingLicenses(true);
    try {
      const licenses = await getAppSubscriptionInfosByUser(userProfile._id);
      setUserLicenses(licenses);
    } catch (error) {
      console.error('Error loading licenses:', error);
    } finally {
      setIsLoadingLicenses(false);
    }
  };

  // When page is loaded, fetch licenses
  useEffect(() => {
    fetchUserLicenses();
  }, [userProfile._id]);

  const form = useForm({
    initialValues: {
      companyName: userProfile.companyName,
      qrCodeColor: userProfile.qrCodeColor,
      username: userProfile.username,
    },
    validate: {
      username: (value) => {
        const error = validateString(value, true);
        return error ? t(error) : null;
      },
      companyName: (value) => {
        const error = validateString(value, false);
        return error ? t(error) : null;
      },
      qrCodeColor: validateHexColor,
    },
  });

  useEffect(() => {
    form.setValues({
      companyName: userProfile.companyName,
      qrCodeColor: userProfile.qrCodeColor,
      username: userProfile.username,
    });

    // Load color suggestions based on company logo if available
    if (userProfile.companyLogo) {
      extractColorsFromLogo(`${cdnCompanyLogos}${userProfile.companyLogo}`);
    }
  }, [userProfile]);

  // Function to extract colors from logo
  const extractColorsFromLogo = async (imageUrl: string) => {
    try {
      // Get 7 prominent colors from the logo
      const colors = await prominent(imageUrl, { amount: 7, format: 'hex' });

      if (Array.isArray(colors)) {
        // Properly convert colors from color.js to string array
        const hexColors = colors.map((color) => {
          // Use directly if already a string, otherwise handle error
          if (typeof color === 'string') {
            return color;
          }
          return '#000000'; // Default color
        });
        setColorSuggestions(hexColors);
      }
    } catch (error) {
      console.error('Error extracting colors from logo:', error);
    }
  };

  // cropping 1:1
  function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop = centerAspectCrop(width, height, 1);
    setCrop(crop);
  };

  async function updateProfile(profileForm: Partial<UserProfile> & { companyLogoFile?: File }) {
    setUpdateSuccess(false);
    setShowSuccessPopup(false);

    const usernameChanged =
      !!profileForm.username && profileForm.username !== userProfile.username;
    const companyNameChanged =
      !!profileForm.companyName && profileForm.companyName !== userProfile.companyName;
    const qrColorChanged =
      !!profileForm.qrCodeColor && profileForm.qrCodeColor !== userProfile.qrCodeColor;
    const logoFile = croppedImageFile || profileForm.companyLogoFile || null;

    if (!usernameChanged && !companyNameChanged && !qrColorChanged && !logoFile) {
      return;
    }

    const buildFormData = () => {
      // Fresh FormData each attempt — a failed fetch may consume the body stream.
      const formData = new FormData();
      if (usernameChanged && profileForm.username) {
        formData.append('username', profileForm.username);
      }
      if (companyNameChanged && profileForm.companyName) {
        formData.append('companyName', profileForm.companyName);
      }
      if (qrColorChanged && profileForm.qrCodeColor) {
        formData.append('qrCodeColor', profileForm.qrCodeColor);
      }
      if (logoFile) {
        formData.append('companyLogoFile', logoFile);
      }
      return formData;
    };

    const id = userProfile._id || (await getUserId());
    // Retry transient gateway failures (same Core/nginx blips as marketplace 502s).
    retryAsync(
      () => patchFormData<Partial<UserProfile>>(userPath + id, buildFormData()),
      { attempts: 3, delaysMs: [800, 2000] }
    )
      .then((data) => {
        if (errorMessage.length > 0) setErrorMessage('');
        setUserProfile(data);
        setCroppedImageFile(null);
        setCroppedImageUrl(null);
        setUpdateSuccess(true);

        // For popup animation
        setShowSuccessPopup(true);
        setPopupPosition(20);
        setPopupOpacity(1);
        // Close popup after 5 seconds
        setTimeout(() => {
          setPopupPosition(-100);
          setPopupOpacity(0);
          setTimeout(() => setShowSuccessPopup(false), 500);
        }, 5000);
      })
      .catch((error: unknown) => {
        if (!isApiError(error)) {
          console.error(error);
          setErrorMessage(t('account.actions.saveFailed'));
          return;
        }
        const { response } = error;
        if (response.status === 400) {
          const data = response.data as { error?: string };
          setErrorMessage(data?.error || t('account.actions.saveFailed'));
          return;
        }
        console.error(error);
        if ([502, 503, 504].includes(response.status)) {
          setErrorMessage(t('account.actions.saveFailedGateway'));
          return;
        }
        if (response.status === 401) {
          setErrorMessage(t('account.actions.saveFailedAuth'));
          return;
        }
        setErrorMessage(t('account.actions.saveFailed'));
      });
  }

  // New function: For FileButton
  const handleFileSelect = (file: File | null) => {
    if (!file) return;

    const mbLimit = 5;
    if (!validateFileSize(file, mbLimit)) {
      setErrorMessage(`Maximale Dateigroesse: ${mbLimit} MB`);
      return;
    }

    if (file.type.startsWith('image/')) {
      // to url
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const dataUrl = reader.result?.toString() || '';
        setImgSrc(dataUrl);

        // Check image proportions
        const img = new Image();
        img.onload = () => {
          // Check image proportions
          const aspectRatio = img.width / img.height;
          const isSquare = Math.abs(aspectRatio - 1) < 0.01; // 1% tolerance

          if (isSquare) {
            // If image is already square, process directly
            createImageFileFromDataUrl(dataUrl, file.name);
            // Extract colors from logo
            extractColorsFromLogo(dataUrl);
          } else {
            // If not square, open crop modal
            setOpenCropModal(true);
          }
        };
        img.src = dataUrl;

        setErrorMessage('');
      });
      reader.readAsDataURL(file);
    } else {
      setErrorMessage('Invalid file type. Please select an image.');
    }
  };

  // Create file from data URL (when cropping is not needed)
  const createImageFileFromDataUrl = (dataUrl: string, fileName: string) => {
    // Convert base64 data to blob
    const byteString = atob(dataUrl.split(',')[1]);
    const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], fileName, { type: mimeString });

    // Set file and URL
    setCroppedImageFile(file);
    const imageUrl = URL.createObjectURL(blob);
    setCroppedImageUrl(imageUrl);
  };

  // For old handleImageUpload function, make the same changes
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; // Get the selected file
    if (!file) return;

    const mbLimit = 5;
    if (file.size > mbLimit * 1024 * 1024) {
      setErrorMessage(`Maximale Dateigroesse: ${mbLimit} MB`);
      e.target.value = '';
      return;
    }

    if (file.type.startsWith('image/')) {
      // to url
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const dataUrl = reader.result?.toString() || '';
        setImgSrc(dataUrl);

        // Check image proportions
        const img = new Image();
        img.onload = () => {
          // Check image proportions
          const aspectRatio = img.width / img.height;
          const isSquare = Math.abs(aspectRatio - 1) < 0.01; // 1% tolerance

          if (isSquare) {
            // If image is already square, process directly
            createImageFileFromDataUrl(dataUrl, file.name);
            // Extract colors from logo
            extractColorsFromLogo(dataUrl);
          } else {
            // If not square, open crop modal
            setOpenCropModal(true);
          }
        };
        img.src = dataUrl;

        setErrorMessage('');
      });
      reader.readAsDataURL(file);
    } else {
      setErrorMessage('Invalid file type. Please select an image.');
    }
  };

  // make a link from the cropped image
  const cropAndSaveImage = async () => {
    if (!completedCrop || !imgRef.current) return;

    const canvas = document.createElement('canvas');
    // make a square canvas
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear the canvas and set the background to white if transparency is an issue
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // draw the image
    ctx.drawImage(
      imgRef.current,
      completedCrop.x,
      completedCrop.y,
      completedCrop.width,
      completedCrop.height,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    // convert canvas to blob
    canvas.toBlob(
      (blob) => {
        if (!blob) return;

        // convert blob to file
        const croppedFile = new File([blob], 'cropped-logo.jpg', { type: 'image/jpeg' });
        setCroppedImageFile(croppedFile);

        // for display create a url
        const croppedUrl = URL.createObjectURL(blob);
        setCroppedImageUrl(croppedUrl);

        // Extract colors from cropped image
        extractColorsFromLogo(croppedUrl);

        setOpenCropModal(false);
      },
      'image/jpeg',
      0.95
    );
  };

  // for closing the modal
  const cancelCrop = () => {
    setOpenCropModal(false);
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  return (
    <SessionAuth>
      <style>
        {`
          /* input style */
          .modern-input .mantine-TextInput-wrapper {
            margin-bottom: 20px;
          }
          
          .modern-input .mantine-TextInput-input {
            height: 46px;
            border-radius: 10px;
            padding-left: 40px;
            border: 1px solid var(--mantine-color-default-border);
            box-shadow: 0 2px 5px rgba(0,0,0,0.05);
            transition: all 0.2s ease;
          }
          
          .modern-input .mantine-TextInput-input:focus {
            border-color: ${userProfile.qrCodeColor};
            box-shadow: 0 3px 8px rgba(0,0,0,0.07);
            transform: translateY(-1px);
          }
          
          .modern-input .mantine-TextInput-label {
            margin-bottom: 6px;
            font-size: 13px;
            font-weight: 500;
            color: var(--mantine-color-dimmed);
          }
          
          .modern-input .mantine-TextInput-section {
            color: var(--mantine-color-gray-6);
          }
          
          .modern-input .mantine-TextInput-input:focus + .mantine-TextInput-section {
            color: ${userProfile.qrCodeColor};
          }
          
          /* Gradient border styles for all cards */
          .gradient-border {
            border-radius: 12px;
            background: linear-gradient(var(--mantine-color-body), var(--mantine-color-body)) padding-box,
                        linear-gradient(45deg, rgba(${userProfile.qrCodeColor
                          .substr(1)
                          .match(/.{2}/g)
                          ?.map((c) => parseInt(c, 16))
                          .join(',')}, 0.2), transparent 60%) border-box;
            border: 1px solid transparent;
            box-shadow: 0 2px 12px rgba(0,0,0,0.06);
            transition: all 0.3s ease;
          }
          
          .gradient-border:hover {
            box-shadow: 0 5px 15px rgba(0,0,0,0.08);
          }
          
          /* Success Popup Animation Styles */
          .success-popup {
            position: fixed;
            top: ${popupPosition}px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1000;
            transition: top 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55), opacity 0.5s ease;
            min-width: 320px;
            opacity: ${popupOpacity};
          }
          
          /* Expandable animation for color input field */
          .color-input-container {
            max-height: ${colorInputExpanded ? '120px' : '0px'};
            opacity: ${colorInputExpanded ? '1' : '0'};
            overflow: hidden;
            transition: max-height 0.4s ease, opacity 0.3s ease, margin 0.4s ease;
            margin-top: ${colorInputExpanded ? '15px' : '0px'};
          }
          
          /* Toggle arrow animation */
          .toggle-arrow {
            transform: rotate(${colorInputExpanded ? '180deg' : '0deg'});
            transition: transform 0.3s ease;
          }
        `}
      </style>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup">
          <Notification
            icon={<IconCheck size={20} />}
            color="teal"
            title={t('account.actions.success')}
            withCloseButton={false}
            radius="lg"
            style={{
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              border: '1px solid #eee',
            }}
          >
            {t('account.actions.updateSuccess')}
          </Notification>
        </div>
      )}

      <Center style={{ ...pageWrapper, paddingTop: '20px' }}>
        <Box mx="auto" style={{ width: '80%' }}>
          <Paper shadow="xs" p="md" radius="md" withBorder mb="xl">
            <Group justify="space-between">
              <Group>
                <PageHeader
                  leading={
                    <Avatar
                      size="xl"
                      src={
                        croppedImageUrl ||
                        (userProfile.companyLogo
                          ? `${cdnCompanyLogos}${userProfile.companyLogo}`
                          : null)
                      }
                      radius="xl"
                      color={userProfile.qrCodeColor}
                    >
                      {!userProfile.companyLogo && !croppedImageUrl
                        ? userProfile.username?.charAt(0).toUpperCase()
                        : null}
                    </Avatar>
                  }
                  title={t('account.header.title', { username: userProfile.username })}
                  subtitle={userProfile.companyName}
                  accentColor={userProfile.qrCodeColor}
                  mb={0}
                />
              </Group>


              <Group justify="center" style={{ flex: 1 }}>
                <Group gap={6} align="center">
                  <Text size="xs" color="dimmed">
                    {t('account.header.displayMode')}:
                  </Text>
                  <ThemeToggle color={userProfile.qrCodeColor} />
                </Group>
              </Group>

              <Group gap={8} wrap="wrap" justify="flex-end">
                <Tooltip label={t('account.actions.changePassword')}>
                  <EditorChipButton
                    accentColor={userProfile.qrCodeColor}
                    leftIcon={<IconKey size={15} stroke={1.75} />}
                    onClick={() => navigate(clientRoutes.accountChangePassword)}
                  >
                    {t('account.actions.changePassword')}
                  </EditorChipButton>
                </Tooltip>
                <Tooltip label={t('account.actions.update')}>
                  <EditorChipButton
                    variant="primary"
                    accentColor={userProfile.qrCodeColor}
                    leftIcon={<IconDeviceFloppy size={15} stroke={1.75} />}
                    onClick={() => formRef.current?.requestSubmit()}
                  >
                    {t('account.actions.update')}
                  </EditorChipButton>
                </Tooltip>
              </Group>
            </Group>
          </Paper>

          <form
            ref={formRef}
            encType="multipart/form-data"
            onSubmit={form.onSubmit(updateProfile)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 15,
            }}
          >
            {/* Personal Information and License Information Side by Side */}
            <SimpleGrid cols={2} spacing="md">
              {/* Personal Information */}
              <Paper shadow="sm" radius="lg" p="md" className="modern-input gradient-border">
                <Title order={4} mb="md" style={{ fontSize: '16px', fontWeight: 500 }}>
                  {t('account.profile.personalInfo')}
                </Title>
                <TextInput
                  leftSection={<IconUser size={18} />}
                  placeholder={t('account.profile.username')}
                  label={t('account.profile.username')}
                  mb="md"
                  {...form.getInputProps('username')}
                />
                <TextInput
                  leftSection={<IconBuilding size={18} />}
                  placeholder={t('account.profile.companyName')}
                  label={t('account.profile.companyName')}
                  mb="sm"
                  value={form.values.companyName}
                  onChange={(e) => form.setFieldValue('companyName', e.target.value)}
                />

                {/* Newsletter Subscription */}
                <NewsletterSubscription color={userProfile.qrCodeColor} />
              </Paper>

              {/* License Information */}
              <Paper shadow="sm" radius="lg" p="md" className="gradient-border">
                <Title order={4} mb="md" style={{ fontSize: '16px', fontWeight: 500 }}>
                  {t('account.licenses.licenseInfo')}
                </Title>

                {isLoadingLicenses ? (
                  <div className={accountClasses.licenseLoading}>{t('account.licenses.loading')}</div>
                ) : userLicenses.length > 0 ? (
                  <div className={accountClasses.licensePanel}>
                    {userLicenses.map((license) => {
                      const expiryDate = new Date(license.subscription.expirationDate);
                      const isActive = expiryDate > new Date();

                      return (
                        <div key={license.subscription._id} className={accountClasses.licenseRow}>
                          <div className={accountClasses.licenseCopy}>
                            <div className={accountClasses.licensePrimary}>
                              <span className={accountClasses.licenseApp}>{license.app.appName}</span>
                              <span className={accountClasses.licenseDivider} aria-hidden>
                                ·
                              </span>
                              <span className={accountClasses.licenseName}>
                                {license.license.licenseName}
                              </span>
                            </div>
                            <div className={accountClasses.licenseExpiry}>
                              {t('common.expiryDate')}{' '}
                              {expiryDate.toLocaleDateString(language === 'de' ? 'de-DE' : undefined)}
                            </div>
                          </div>
                          <div
                            className={`${accountClasses.licenseStatus} ${isActive ? accountClasses.licenseStatusActive : ''}`}
                          >
                            <span
                              className={`${accountClasses.statusDot} ${isActive ? accountClasses.statusDotActive : ''}`}
                              aria-hidden
                            />
                            {isActive ? t('common.active') : t('common.inactive')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={accountClasses.licenseEmpty}>{t('account.licenses.none')}</div>
                )}

                <Group justify="flex-end" mt="lg">
                  <Tooltip label={t('account.licenses.manage')}>
                    <EditorChipButton
                      accentColor={userProfile.qrCodeColor}
                      leftIcon={<IconRefresh size={15} stroke={1.75} />}
                      onClick={fetchUserLicenses}
                    >
                      {t('account.actions.refresh')}
                    </EditorChipButton>
                  </Tooltip>
                </Group>
              </Paper>
            </SimpleGrid>

            {/* Color Selection and Logo Section Side by Side */}
            <SimpleGrid cols={2} spacing="md">
              {/* Color Selection Section */}
              <Card shadow="xs" p="lg" radius="md" className="gradient-border">
                <Card.Section withBorder inheritPadding py="xs">
                  <Text style={{ fontSize: '16px', fontWeight: 500 }}>
                    {t('account.colors.settings')}
                  </Text>
                </Card.Section>

                <Box mt="md">
                  <Text size="sm" mb="xs" color="dimmed">
                    {t('account.colors.preview')}:
                  </Text>
                  <ColorPicker
                    format="hex"
                    swatches={colorSuggestions}
                    fullWidth
                    value={form.values.qrCodeColor}
                    onChange={(color) => form.setFieldValue('qrCodeColor', color)}
                    size="sm"
                    mb="md"
                  />

                  <Text size="xs" color="dimmed" mb="md">
                    {t('account.colors.suggestions')}
                  </Text>

                  {/* Clickable area for expandable section */}
                  <Group
                    justify="flex-end"
                    onClick={() => setColorInputExpanded(!colorInputExpanded)}
                    style={{
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      marginTop: '5px',
                    }}
                  >
                    <Text
                      size="xs"
                      color="dimmed"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {t('account.colors.manualInput')}
                      <span
                        className="toggle-arrow"
                        style={{ marginLeft: '5px', display: 'inline-flex' }}
                      >
                        <IconChevronDown size={14} />
                      </span>
                    </Text>
                  </Group>

                  {/* Expandable input area */}
                  <div className="color-input-container">
                    <TextInput
                      placeholder="#RRGGBB"
                      label={t('account.colors.qrCodeColor')}
                      leftSection={<IconPalette size={16} />}
                      {...form.getInputProps('qrCodeColor')}
                      variant="filled"
                    />
                  </div>
                </Box>
              </Card>

              {/* Logo Upload Section */}
              <Card shadow="xs" p="lg" radius="md" className="gradient-border" h="100%">
                <Card.Section withBorder inheritPadding py="xs">
                  <Group justify="space-between">
                    <Text style={{ fontSize: '16px', fontWeight: 500 }}>
                      {t('account.logo.companyLogo')}
                    </Text>
                    <FileButton onChange={handleFileSelect} accept="image/png,image/jpeg,image/jpg">
                      {(props) => (
                        <EditorChipButton
                          {...props}
                          accentColor={userProfile.qrCodeColor}
                          leftIcon={<IconUpload size={15} stroke={1.75} />}
                        >
                          {t('account.logo.upload')}
                        </EditorChipButton>
                      )}
                    </FileButton>
                  </Group>
                </Card.Section>

                <Group justify="center" py="lg" h="calc(100% - 50px)" align="center">
                  {croppedImageUrl ? (
                    <img
                      src={croppedImageUrl}
                      alt={t('account.logo.companyLogo')}
                      style={{
                        maxWidth: '180px',
                        maxHeight: '180px',
                        aspectRatio: '1/1',
                        borderRadius: '4px',
                      }}
                    />
                  ) : userProfile.companyLogo ? (
                    <img
                      src={`${cdnCompanyLogos}${userProfile.companyLogo}`}
                      alt={t('account.logo.companyLogo')}
                      style={{ maxWidth: '180px', maxHeight: '180px', borderRadius: '4px' }}
                    />
                  ) : (
                    <Box
                      style={{
                        width: 150,
                        height: 150,
                        backgroundColor: 'var(--mantine-color-default)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        color: 'var(--mantine-color-dimmed)',
                        border: '1px solid var(--mantine-color-default-border)',
                      }}
                    >
                      <Text ta="center">{t('account.logo.none')}</Text>
                    </Box>
                  )}
                </Group>
              </Card>
            </SimpleGrid>

            {/* Status and Update Button */}
            <Group mt="lg" justify="space-between" align="center">
              <div>
                {errorMessage && (
                  <Text color="red" fw={500}>
                    {errorMessage}
                  </Text>
                )}
              </div>
            </Group>
          </form>
        </Box>
      </Center>

      <Modal opened={openCropModal} onClose={cancelCrop} title={t('account.logo.cropTitle')} size="lg">
        <div style={{ maxWidth: '100%', textAlign: 'center' }}>
          <Text mb="md">{t('account.logo.cropInstructions')}</Text>
          {imgSrc && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              circularCrop={false}
            >
              <img
                ref={imgRef}
                alt={t('account.logo.cropTitle')}
                src={imgSrc}
                onLoad={onImageLoad}
                style={{ maxWidth: '100%' }}
              />
            </ReactCrop>
          )}
          <Group justify="center" mt="xl" gap={8}>
            <EditorChipButton
              variant="primary"
              accentColor={userProfile.qrCodeColor}
              leftIcon={<IconCheck size={15} stroke={1.75} />}
              onClick={cropAndSaveImage}
              disabled={!completedCrop?.width || !completedCrop?.height}
            >
              {t('account.logo.cropAndSave')}
            </EditorChipButton>
            <EditorChipButton accentColor={userProfile.qrCodeColor} onClick={cancelCrop}>
              {t('account.actions.cancel')}
            </EditorChipButton>
          </Group>
        </div>
      </Modal>
    </SessionAuth>
  );
}

// toggle for dark and light mode
function ThemeToggle({ color }: { color: string }) {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  return (
    <div
      onClick={() => toggleColorScheme()}
      style={{
        width: '60px',
        height: '30px',
        position: 'relative',
        borderRadius: '40px',
        display: 'inline-block',
        cursor: 'pointer',
        padding: '3px',
        backgroundColor: isDark ? '#0A1929' : '#F8F9FA',
        overflow: 'hidden',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
      }}
    >
      <div
        style={{
          left: isDark ? 'calc(100% - 28px)' : '3px',
          width: '24px',
          height: '24px',
          position: 'absolute',
          borderRadius: '50%',
          backgroundColor: isDark ? '#FFFFFF' : '#FFD43B',
          transition: 'all 0.3s ease',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
      >
        {isDark ? (
          <IconMoon size={16} style={{ color: '#0A1929' }} />
        ) : (
          <IconSun size={16} style={{ color: '#FFF' }} stroke={3} />
        )}
      </div>

    </div>
  );
}
